import { getCachedConfig } from '@/api/config'

const DEMO_MAP_ID = 'DEMO_MAP_ID'

type Coord = { lat: number; lng: number }

/** the cloud-console vector map id carries the per-color-scheme styles. falls
 * back to google's demo id until /v1/config has loaded a real one.
 */
export function resolveMapId(): string {
  return getCachedConfig()?.googleMapsMapId ?? DEMO_MAP_ID
}

export function parseCoord(
  latitude: string | null,
  longitude: string | null
): Coord | null {
  if (latitude === null || longitude === null) return null
  const lat = Number(latitude)
  const lng = Number(longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

/** builds a themed map with our standard pin at center plus a reset-view
 * control. colorScheme can only be set at construction, so callers wipe the
 * container and rebuild on theme change. pinScale shrinks the marker for the
 * smaller mini-map.
 */
export function mountSupplierMap(
  container: HTMLDivElement,
  center: Coord,
  options: { mapId: string; isDark: boolean; zoom: number; pinScale?: number }
): void {
  const map = new google.maps.Map(container, {
    center,
    zoom: options.zoom,
    /** mapId carries the cloud-console style assignments (one style per color
     * scheme). colorScheme picks which is rendered. google.maps.ColorScheme
     * isn't always typed on the imported sdk, so the string form passes
     * through directly.
     */
    mapId: options.mapId,
    colorScheme: options.isDark ? 'DARK' : 'LIGHT',
    mapTypeControl: false,
    clickableIcons: false,
    /* the map sits inside a scrolling page: without this a one-finger drag
     * pans the map and the page can never be scrolled past it. */
    gestureHandling: 'cooperative'
  })

  const pinElement = new google.maps.marker.PinElement({
    background: '#1e3a8a',
    borderColor: '#172554',
    glyphColor: '#ffffff',
    ...(options.pinScale ? { scale: options.pinScale } : {})
  })
  new google.maps.marker.AdvancedMarkerElement({
    position: center,
    map,
    content: pinElement
  })

  mountResetControl(map, () => {
    map.setCenter(center)
    map.setZoom(options.zoom)
  })
}

/** injects a "reset view" button into the map's native control layer so it
 * sits alongside zoom + pegman. raw dom because google maps controls live
 * outside the react tree.
 */
function mountResetControl(map: google.maps.Map, onClick: () => void): void {
  const button = document.createElement('button')
  button.type = 'button'
  button.title = 'Reset view'
  button.setAttribute('aria-label', 'Reset view')
  /** lucide LocateFixed inlined as svg so we don't need to render react
   * outside the component tree.
   */
  button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="3"/></svg>`
  Object.assign(button.style, {
    alignItems: 'center',
    background: 'var(--card)',
    border: 'none',
    borderRadius: '2px',
    boxShadow: '0 1px 4px -1px rgba(0,0,0,0.3)',
    color: 'var(--ink-muted)',
    cursor: 'pointer',
    display: 'flex',
    height: '40px',
    justifyContent: 'center',
    margin: '10px',
    padding: '8px',
    width: '40px'
  })
  button.addEventListener('mouseenter', () => {
    button.style.color = 'var(--ink)'
  })
  button.addEventListener('mouseleave', () => {
    button.style.color = 'var(--ink-muted)'
  })
  button.addEventListener('click', onClick)
  map.controls[google.maps.ControlPosition.RIGHT_TOP]!.push(button)
}
